import {
  ChevronRightOutlined,
  EditLocationAltOutlined,
  EmailRounded,
  ForwardToInboxOutlined,
  LinkOutlined,
  LocalOfferOutlined,
  PersonSearchRounded,
  PhoneRounded,
  SubdirectoryArrowRightOutlined,
  SyncOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Typography,
  alpha,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEmpty, isNil, size } from "lodash";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePermissionsHook } from "../../../hooks/usePermissionsHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import QuickActionButton from "../../borrowerDetails/components/QuickActionButton";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import AssociationsEditor from "../../associations/AssociationsEditor";
import { formatBorrowerObjectFullName } from "../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import CustomSnackbar from "../../../_src_shared/components/CustomSnackbar";
import { camelCaseToSentence } from "../../../utils/strings/camelCaseToSentence";

// ── Palette (shared with DrilldownSidebarAddBorrower) ───────────────────────

const palette = {
  indigo: "#4f46e5",
  indigoLight: "#eef2ff",
  emerald: "#059669",
  emeraldLight: "#ecfdf5",
  amber: "#d97706",
  amberLight: "#fffbeb",
  slate50: "#f8fafc",
  slate100: "#f1f5f9",
  slate200: "#e2e8f0",
  slate400: "#94a3b8",
  slate500: "#64748b",
  slate700: "#334155",
  slate900: "#0f172a",
};

// ── Confirmation configs ────────────────────────────────────────────────────

const CONFIRMATIONS = {
  pullFromPortal: {
    title: "Pull Data from Portal",
    description:
      "This will overwrite the borrower's contact fields, validated experience, and bank details on this loan with data from their portal profile. Any portal documents will also be copied into this loan's file storage.",
    confirmLabel: "Pull & Overwrite",
    severity: "warning",
  },
  syncToPortal: {
    title: "Sync to Portal",
    description:
      "This will push this borrower's underwriting data (FICO, experience, liquidity, properties, entity info, and documents) to their linked portal user profile. Existing portal data for these fields will be overwritten.",
    confirmLabel: "Sync to Portal",
    severity: "warning",
  },
  inviteToPortal: {
    title: "Invite to Portal",
    description:
      "This will create a new borrower portal account using this borrower's contact information and send them an invitation email. The new portal user will be automatically linked to this borrower and loan.",
    confirmLabel: "Send Invite",
    severity: "info",
  },
};

// ── Main component ──────────────────────────────────────────────────────────

const LoanBorrowerSidebarActions = memo(
  ({ borrower, dealId, onEditAddress }) => {
    const [showModal, setShowModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [loading, setLoading] = useState(false);

    // Link flow state
    const [showLinkSearch, setShowLinkSearch] = useState(false);

    const dispatch = useDispatch();
    const loanDrilldown = useSelector((state) => state.loanDrilldown);
    const { canAccessBorrowerPortal } = usePermissionsHook();
    const {
      linkBorrowerToPortalUser,
      syncBorrowerToPortalUser,
      syncPortalUserToBorrower,
      inviteBorrowerToPortalFromLoan,
    } = useUnderwritingHook();
    const { loading: assigningFiles, associateDocumentWithObjects } =
      useLoanFilesHook();

    const isLinked = !!borrower?.borrowerPortalUserId;
    const borrowerId = borrower?._id;

    // Pre-checked + locked rows in the file picker — already-linked
    // loanDocuments on this borrower can't be re-toggled here.
    const existingFileAssociations = useMemo(
      () => ({
        loanDocuments: borrower?.associations?.loanDocuments || [],
      }),
      [borrower?.associations?.loanDocuments],
    );

    const handleAssignFiles = useCallback(
      (selected) => {
        const newDocIds = selected?.loanDocuments || [];
        if (newDocIds.length === 0) {
          setShowModal(false);
          return;
        }
        associateDocumentWithObjects({
          dealId,
          loanDocumentsIds: newDocIds,
          borrowerIds: borrowerId ? [borrowerId] : undefined,
          onSuccessFn: (d) => {
            dispatch(loanDrilldownSet(d));
            setShowModal(false);
          },
        });
      },
      [associateDocumentWithObjects, dealId, borrowerId, dispatch],
    );

    // ── Handle link: user selected from search results ──
    const handleLinkUser = useCallback(
      (portalUser) => {
        setLoading(true);
        linkBorrowerToPortalUser({
          dealId,
          borrowerId,
          data: {
            portalUserId: portalUser?._id,
            emailAddress: portalUser?.emailAddress,
          },
          onSuccessFn: (resData) => {
            if (resData?.loanNumber) {
              dispatch(loanDrilldownSet(resData));
            }
            setLoading(false);
            setShowLinkSearch(false);
          },
          onFailFn: () => {
            setLoading(false);
          },
        });
      },
      [dealId, borrowerId, linkBorrowerToPortalUser, dispatch],
    );

    // ── Handle confirmation for non-link actions ──
    const handleConfirm = useCallback(() => {
      if (!pendingAction) return;

      setLoading(true);

      const onDone = () => {
        setLoading(false);
        setPendingAction(null);
      };

      const handlers = {
        pullFromPortal: () =>
          syncPortalUserToBorrower({
            dealId,
            borrowerId,
            onSuccessFn: (res) => {
              if (res?.deal) {
                dispatch(loanDrilldownSet(res.deal));
              }
              const syncedFields = [
                ...(res?.synced_scalar_fields || []),
                ...(res?.synced_arrays || []),
              ].map(camelCaseToSentence);
              const message = isEmpty(syncedFields)
                ? "Pulled from portal — no fields needed updating"
                : `Pulled from portal — updated ${syncedFields.join(", ")}`;
              enqueueSnackbar(message, {
                autoHideDuration: 4500,
                anchorOrigin: { vertical: "bottom", horizontal: "right" },
                content: (key, msg) => (
                  <CustomSnackbar
                    id={key}
                    message={msg}
                    variant="success"
                    closeSnackbar={() => closeSnackbar(key)}
                  />
                ),
              });
              onDone();
            },
            onFailFn: onDone,
          }),
        syncToPortal: () =>
          syncBorrowerToPortalUser({
            dealId,
            borrowerId,
            onSuccessFn: () => onDone(),
            onFailFn: onDone,
          }),
        inviteToPortal: () =>
          inviteBorrowerToPortalFromLoan({
            dealId,
            borrowerId,
            onSuccessFn: () => onDone(),
            onFailFn: onDone,
          }),
      };

      handlers[pendingAction]?.();
    }, [
      pendingAction,
      dealId,
      borrowerId,
      dispatch,
      syncBorrowerToPortalUser,
      syncPortalUserToBorrower,
      inviteBorrowerToPortalFromLoan,
    ]);

    const confirmConfig = pendingAction ? CONFIRMATIONS[pendingAction] : null;

    if (isNil(borrower)) return null;

    return (
      <Flx column gap={1}>
        <Flx wrap g={1}>
          <AssociationsEditor
            open={showModal}
            loan={loanDrilldown}
            enabledFields={["loanDocuments"]}
            existing={existingFileAssociations}
            title="Assign files to borrower"
            hostLabel={formatBorrowerObjectFullName(borrower) || "Borrower"}
            saving={assigningFiles}
            onSave={handleAssignFiles}
            onClose={() => setShowModal(false)}
          />

          {/* <QuickActionButton
          onClick={onEditAddress}
          startIcon={<EditLocationAltOutlined className="thin" />}
        >
          Edit Address
        </QuickActionButton>

        <QuickActionButton
          onClick={() => setShowModal(true)}
          startIcon={<LocalOfferOutlined className="thin" />}
        >
          Associate Files
        </QuickActionButton> */}

          {isLinked ? (
            <>
              <QuickActionButton
                onClick={() => setPendingAction("pullFromPortal")}
                startIcon={<SubdirectoryArrowRightOutlined className="thin" />}
                tooltip="Updates this loan using info from the borrower's account — the login they use to sign into the Borrower Portal. Overwrites their contact details, validated experience, and bank details on this loan, and copies any documents they uploaded into this loan's files. Direction: Borrower's Account → Loan."
              >
                Update Loan from Borrower's Portal Account
                {/* Pull from Portal */}
              </QuickActionButton>

              <QuickActionButton
                onClick={() => setPendingAction("syncToPortal")}
                startIcon={<SyncOutlined className="thin" />}
                tooltip="Updates the borrower's account — the login they use to sign into the Borrower Portal — using this loan's data. Pushes FICO, experience, liquidity, properties, entity info, and documents to their account, overwriting what's there. Direction: Loan → Borrower's Account."
              >
                {/* Sync to Portal */}
                Update Borrower's Portal Account from Loan
              </QuickActionButton>
            </>
          ) : (
            <>
              <QuickActionButton
                onClick={() => {
                  setPendingAction(null);
                  setShowLinkSearch(true);
                }}
                startIcon={<LinkOutlined className="thin" />}
                tooltip="Connects this borrower to an existing account — the login they use to sign into the Borrower Portal — so the loan and that account are tied together. Searches by the borrower's email; no new account is created."
              >
                Connect to Existing Borrower Portal Account
                {/* Link Pre-Existing Borrower Portal User */}
              </QuickActionButton>
              {canAccessBorrowerPortal && (
                <QuickActionButton
                  onClick={() => {
                    setShowLinkSearch(false);
                    setPendingAction("inviteToPortal");
                  }}
                  startIcon={<ForwardToInboxOutlined className="thin" />}
                  tooltip="Creates a brand-new account for the borrower — the login they'll use to sign into the Borrower Portal — from their contact info, and emails them an invitation to set it up. The new account is automatically linked to this borrower and loan."
                >
                  Invite Borrower to Create Account
                  {/* Invite to Portal */}
                </QuickActionButton>
              )}
            </>
          )}
        </Flx>

        {/* ── Link search panel ── */}
        <Collapse in={showLinkSearch && !isLinked} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <LinkPortalUserSearchPanel
              borrower={borrower}
              onSelectUser={handleLinkUser}
              onCancel={() => setShowLinkSearch(false)}
              linking={loading}
            />
          </Box>
        </Collapse>

        {/* ── Confirmation alert (for non-link actions) ── */}
        <Collapse in={!!pendingAction && !showLinkSearch} unmountOnExit>
          {confirmConfig && (
            <Alert
              severity={confirmConfig.severity}
              icon={<WarningAmberRounded sx={{ fontSize: 20, mt: 0.3 }} />}
              sx={{
                mt: 1,
                alignItems: "flex-start",
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              <Flx column g={1}>
                <Txt
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "inherit",
                  }}
                >
                  {confirmConfig.title}
                </Txt>
                <Txt
                  sx={{
                    fontSize: 12.5,
                    color: "inherit",
                    lineHeight: 1.5,
                    opacity: 0.85,
                  }}
                >
                  {confirmConfig.description}
                </Txt>
                <Flx ac g={1} sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    loading={loading}
                    loadingPosition="end"
                    onClick={handleConfirm}
                    color={
                      confirmConfig.severity === "warning"
                        ? "warning"
                        : "primary"
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {confirmConfig.confirmLabel}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setPendingAction(null)}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      fontSize: 12,
                      color: grey[700],
                    }}
                  >
                    Cancel
                  </Button>
                </Flx>
              </Flx>
            </Alert>
          )}
        </Collapse>
      </Flx>
    );
  },
);

// ── Link Portal User Search Panel ───────────────────────────────────────────
// Searches by the borrower's email automatically on mount, then displays
// results as selectable cards. The user picks one to fire linkBorrowerToPortalUser.

const LinkPortalUserSearchPanel = ({
  borrower,
  onSelectUser,
  onCancel,
  linking,
}) => {
  const { searchBorrowerPortalUsers } = useUnderwritingHook();
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchedEmail, setSearchedEmail] = useState(null);

  // Auto-search on first render or when borrower email changes
  const handleSearch = useCallback(
    (email) => {
      if (!email) return;
      setSearching(true);
      setSearchedEmail(email);
      searchBorrowerPortalUsers({
        data: { emailAddress: email },
        onSuccessFn: (res) => {
          setResults(res || []);
          setSearching(false);
        },
        onFailFn: () => {
          setResults([]);
          setSearching(false);
        },
      });
    },
    [searchBorrowerPortalUsers],
  );

  // Search immediately if we haven't yet
  if (isNil(results) && !searching) {
    handleSearch(borrower?.emailAddress);
  }

  return (
    <Box
      sx={{
        border: `1px solid ${palette.slate200}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: palette.slate50,
          borderBottom: `1px solid ${palette.slate100}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Flx ac g={1}>
          <PersonSearchRounded sx={{ fontSize: 18, color: palette.slate500 }} />
          <Flx column>
            <Txt
              sx={{ fontSize: 13, fontWeight: 600, color: palette.slate700 }}
            >
              Link to Portal User
            </Txt>
            <Txt sx={{ fontSize: 11, color: palette.slate400 }}>
              {searchedEmail
                ? `Searching for "${searchedEmail}"`
                : "Searching..."}
            </Txt>
          </Flx>
        </Flx>
        <Button
          size="small"
          variant="text"
          onClick={onCancel}
          disabled={linking}
          sx={{
            textTransform: "none",
            fontSize: 11,
            color: palette.slate500,
            minWidth: 0,
          }}
        >
          Cancel
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5 }}>
        {searching ? (
          <Flx ac jc sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Flx>
        ) : isEmpty(results) ? (
          <Flx column center sx={{ textAlign: "center", py: 3 }}>
            <PersonSearchRounded
              sx={{ fontSize: 32, mb: 1, color: palette.slate200 }}
            />
            <Txt
              sx={{
                fontSize: 13,
                color: palette.slate400,
                mb: 1,
              }}
            >
              No portal users found for this email
            </Txt>
            <Txt
              sx={{
                fontSize: 11,
                color: palette.slate400,
                mb: 2,
              }}
            >
              You can invite this borrower to create a new portal account
              instead.
            </Txt>
          </Flx>
        ) : (
          <Flx column g={1}>
            <Txt
              sx={{
                fontSize: 11,
                color: palette.slate400,
                fontWeight: 500,
                px: 0.5,
              }}
            >
              {size(results)} user{size(results) !== 1 ? "s" : ""} found —
              select one to link
            </Txt>
            {(results || []).map((user) => (
              <LinkableUserCard
                key={user?._id}
                user={user}
                onSelect={() => onSelectUser(user)}
                disabled={linking}
              />
            ))}
          </Flx>
        )}
      </Box>
    </Box>
  );
};

// ── Linkable User Card ──────────────────────────────────────────────────────
// Similar to BorrowerUserCard from DrilldownSidebarAddBorrower but compact,
// with a "Link" action on the right.

const LinkableUserCard = ({ user, onSelect, disabled }) => {
  const isActive = user?.activeStatus === "active" || user?.hasSignedIn;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const initials = fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box
      onClick={disabled ? undefined : onSelect}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: `1px solid ${palette.slate200}`,
        borderRadius: 1.5,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.15s ease",
        "&:hover": disabled
          ? {}
          : {
              borderColor: alpha(palette.indigo, 0.3),
              boxShadow: `0 2px 8px ${alpha(palette.indigo, 0.08)}`,
            },
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${palette.indigo}, #7c3aed)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 700,
          fontSize: "0.72rem",
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Flx ac g={0.8}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.84rem",
              color: palette.slate900,
              lineHeight: 1.2,
            }}
            noWrap
          >
            {fullName || "—"}
          </Typography>
          <Chip
            size="small"
            label={isActive ? "Active" : "Staged"}
            sx={{
              height: 18,
              fontSize: "0.6rem",
              fontWeight: 700,
              bgcolor: isActive ? palette.emeraldLight : palette.amberLight,
              color: isActive ? palette.emerald : palette.amber,
              border: `1px solid ${isActive ? alpha(palette.emerald, 0.2) : alpha(palette.amber, 0.2)}`,
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </Flx>

        <Flx ac g={1.5} sx={{ mt: 0.3 }}>
          {user?.emailAddress && (
            <Flx ac g={0.4}>
              <EmailRounded sx={{ fontSize: 11, color: palette.slate400 }} />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: palette.slate500,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
                noWrap
              >
                {user.emailAddress}
              </Typography>
            </Flx>
          )}
          {user?.phone && (
            <Flx ac g={0.4}>
              <PhoneRounded sx={{ fontSize: 11, color: palette.slate400 }} />
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: palette.slate500,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
                noWrap
              >
                {user.phone}
              </Typography>
            </Flx>
          )}
        </Flx>
      </Box>

      {/* Action hint */}
      <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
        <Txt
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: palette.indigo,
          }}
        >
          Link
        </Txt>
        <ChevronRightOutlined sx={{ fontSize: 16, color: palette.slate400 }} />
      </Flx>
    </Box>
  );
};

export default LoanBorrowerSidebarActions;
